/**
 * Plugin Action Registry (Frontend)
 *
 * Zustand store that holds registered plugin actions on the frontend.
 * Actions are populated from:
 * 1. Backend GET /api/actions endpoint (on startup)
 * 2. Plugin UI modules that export an `actions` array
 *
 * Editors (like plugins) use this registry to discover and render
 * dynamic action buttons based on content shape.
 */

import { create } from 'zustand';

// ── Types (mirroring SDK types for frontend use) ──────────────

/**
 * Action category for UI grouping.
 */
export type ActionCategory =
  | 'generate'
  | 'transform'
  | 'analyze'
  | 'extract'
  | 'export'
  | 'chat'
  | 'utility';

/**
 * How the action result should be displayed.
 */
export type ActionUIMode = 'inline' | 'panel' | 'modal' | 'replace' | 'toast';

/**
 * Whether the action requires user interaction during execution.
 * - 'none'        — headless (default)
 * - 'modal'       — interactive: frontend must show a modal dialog
 * - 'panel'       — interactive: frontend must show a side panel
 * - 'inline-edit' — interactive: frontend must show an inline editor
 */
export type ActionInteractionMode = 'none' | 'modal' | 'panel' | 'inline-edit';

/**
 * Action descriptor — static metadata about a registered action.
 */
export interface ActionDescriptor {
  id: string;
  pluginId: string;
  name: string;
  label: string;
  description: string;
  icon?: string;
  category: ActionCategory;
  accepts: string[];
  produces: string;
  uiMode?: ActionUIMode;
  /** Whether this action requires user interaction (default: 'none') */
  interaction?: ActionInteractionMode;
  uiComponent?: string;
  shortcut?: string;
  priority?: number;
  entityType?: string;
  requiredPermission?: string;
  timeout?: number;
}

/**
 * Action input sent to the backend handler.
 */
export interface ActionInput {
  contentShape: string;
  content: string | Record<string, any>;
  metadata: Record<string, any>;
  context?: {
    entityId?: string;
    entityType?: string;
    sourcePluginId?: string;
    [key: string]: any;
  };
}

/**
 * Action output returned from the backend handler.
 */
export interface ActionOutput {
  outputShape: string;
  content: string | Record<string, any>;
  metadata?: Record<string, any>;
  partial?: boolean;
}

// ── Content shape matching ────────────────────────────────────

function contentShapeMatches(shape: string, pattern: string): boolean {
  if (shape === pattern) return true;
  if (pattern.endsWith('.*')) {
    const prefix = pattern.slice(0, -2);
    return shape.startsWith(prefix + '.');
  }
  if (shape.endsWith('.*')) {
    const prefix = shape.slice(0, -2);
    return pattern.startsWith(prefix + '.');
  }
  return false;
}

// ── Store ─────────────────────────────────────────────────────

interface ActionRegistryState {
  /** All registered actions keyed by action ID */
  actions: Map<string, ActionDescriptor>;

  /** Custom UI components for action results */
  uiComponents: Map<string, React.ComponentType<any>>;

  /**
   * Wave 22.13.3: Runtime action handlers registered via PluginActionsAPI.register().
   * Keyed by "{pluginId}:{actionName}" so the invocation pipeline can call
   * the plugin's handler directly instead of always going to the backend.
   */
  handlers: Map<string, (...args: any[]) => any>;

  /** Loading state */
  isLoading: boolean;

  /** Error state */
  error: string | null;

  // ── Actions ──

  /** Register a single action descriptor */
  registerAction: (descriptor: ActionDescriptor) => void;

  /** Register multiple action descriptors at once */
  registerActions: (descriptors: ActionDescriptor[]) => void;

  /** Unregister all actions from a plugin */
  unregisterPlugin: (pluginId: string) => void;

  /** Register a custom UI component for action results */
  registerUIComponent: (name: string, component: React.ComponentType<any>) => void;

  /**
   * Wave 22.13.3: Register a runtime handler for an action.
   * Called by host-ui-api.ts when a plugin calls actions.register() in setup().
   */
  registerHandler: (pluginId: string, actionName: string, handler: (...args: any[]) => any) => void;

  /**
   * Wave 22.13.3: Retrieve a registered runtime handler for an action.
   * Returns undefined if no handler was registered (action is backend-only).
   */
  getHandler: (pluginId: string, actionName: string) => ((...args: any[]) => any) | undefined;

  /** Get all actions that accept a given content shape */
  getActionsForContent: (contentShape: string) => ActionDescriptor[];

  /** Get all actions targeting a specific entity type */
  getActionsForEntityType: (entityType: string) => ActionDescriptor[];

  /** Get all actions grouped by category */
  getActionsByCategory: (contentShape?: string) => Record<ActionCategory, ActionDescriptor[]>;

  /** Get a single action by ID */
  getAction: (actionId: string) => ActionDescriptor | undefined;

  /** Get a custom UI component by name */
  getUIComponent: (name: string) => React.ComponentType<any> | undefined;

  /** Get all registered actions as an array */
  getAllActions: () => ActionDescriptor[];

  /** Clear all registrations */
  clear: () => void;

  /** Set loading state */
  setLoading: (loading: boolean) => void;

  /** Set error state */
  setError: (error: string | null) => void;

  /** Sync from backend API response */
  syncFromBackend: (actions: ActionDescriptor[]) => void;
}

export const useActionRegistryStore = create<ActionRegistryState>((set, get) => ({
  actions: new Map(),
  uiComponents: new Map(),
  handlers: new Map(),
  isLoading: false,
  error: null,

  registerHandler: (pluginId: string, actionName: string, handler: (...args: any[]) => any) => {
    set((state) => {
      const newHandlers = new Map(state.handlers);
      newHandlers.set(`${pluginId}:${actionName}`, handler);
      return { handlers: newHandlers };
    });
  },

  getHandler: (pluginId: string, actionName: string) => {
    return get().handlers.get(`${pluginId}:${actionName}`);
  },

  registerAction: (descriptor: ActionDescriptor) => {
    set((state) => {
      const newActions = new Map(state.actions);
      newActions.set(descriptor.id, descriptor);
      return { actions: newActions };
    });
  },

  registerActions: (descriptors: ActionDescriptor[]) => {
    set((state) => {
      const newActions = new Map(state.actions);
      for (const d of descriptors) {
        newActions.set(d.id, d);
      }
      return { actions: newActions };
    });
  },

  unregisterPlugin: (pluginId: string) => {
    set((state) => {
      const newActions = new Map(state.actions);
      for (const [id, action] of newActions) {
        if (action.pluginId === pluginId) {
          newActions.delete(id);
        }
      }
      return { actions: newActions };
    });
  },

  registerUIComponent: (name: string, component: React.ComponentType<any>) => {
    set((state) => {
      const newComponents = new Map(state.uiComponents);
      newComponents.set(name, component);
      return { uiComponents: newComponents };
    });
  },

  getActionsForContent: (contentShape: string) => {
    const results: ActionDescriptor[] = [];
    for (const action of get().actions.values()) {
      const matches = action.accepts.some((accepted) =>
        contentShapeMatches(contentShape, accepted),
      );
      if (matches) {
        results.push(action);
      }
    }
    return results.sort((a, b) => (a.priority ?? 50) - (b.priority ?? 50));
  },

  getActionsForEntityType: (entityType: string) => {
    const results: ActionDescriptor[] = [];
    for (const action of get().actions.values()) {
      if (action.entityType === entityType) {
        results.push(action);
      }
    }
    return results.sort((a, b) => (a.priority ?? 50) - (b.priority ?? 50));
  },

  getActionsByCategory: (contentShape?: string) => {
    const all = contentShape
      ? get().getActionsForContent(contentShape)
      : get().getAllActions();

    const grouped: Record<ActionCategory, ActionDescriptor[]> = {
      generate: [],
      transform: [],
      analyze: [],
      extract: [],
      export: [],
      chat: [],
      utility: [],
    };

    for (const action of all) {
      const cat = action.category || 'utility';
      if (grouped[cat]) {
        grouped[cat].push(action);
      } else {
        grouped.utility.push(action);
      }
    }

    return grouped;
  },

  getAction: (actionId: string) => {
    return get().actions.get(actionId);
  },

  getUIComponent: (name: string) => {
    return get().uiComponents.get(name);
  },

  getAllActions: () => {
    return Array.from(get().actions.values()).sort(
      (a, b) => (a.priority ?? 50) - (b.priority ?? 50),
    );
  },

  clear: () => {
    set({ actions: new Map(), uiComponents: new Map(), handlers: new Map() });
  },

  setLoading: (loading: boolean) => {
    set({ isLoading: loading });
  },

  setError: (error: string | null) => {
    set({ error });
  },

  syncFromBackend: (actions: ActionDescriptor[]) => {
    const newActions = new Map<string, ActionDescriptor>();
    for (const action of actions) {
      newActions.set(action.id, action);
    }
    set({ actions: newActions, isLoading: false });
  },
}));
